import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface OpenRiqueOptions {
  prompt?: string;
  companyId?: string;
}

interface RiqueChatState {
  isOpen: boolean;
  isMinimized: boolean;
  isExpanded: boolean;
  showHistory: boolean;
  pendingPrompt: string | null;
  companyId: string;
  pageContext: string;
  routePath: string;
}

interface RiqueChatActions {
  openRique: (options?: OpenRiqueOptions) => void;
  closeRique: () => void;
  toggleRique: () => void;
  toggleMinimize: () => void;
  toggleExpanded: () => void;
  setExpanded: (expanded: boolean) => void;
  setShowHistory: (show: boolean) => void;
  setCompanyId: (companyId: string) => void;
  setPageContext: (pageContext: string) => void;
  setRoutePath: (routePath: string) => void;
  consumePendingPrompt: () => string | null;
}

export const useRiqueChatStore = create<RiqueChatState & RiqueChatActions>()(
  persist(
    (set, get) => ({
      isOpen: false,
      isMinimized: false,
      isExpanded: false,
      showHistory: false,
      pendingPrompt: null,
      companyId: '',
      pageContext: '',
      routePath: '',

      openRique: (options) => {
        set({
          isOpen: true,
          isMinimized: false,
          isExpanded: false,
          showHistory: false,
          pendingPrompt: options?.prompt?.trim() || null,
          companyId: options?.companyId ?? get().companyId,
        });
      },

      closeRique: () => {
        set({ isOpen: false, isMinimized: false, isExpanded: false, showHistory: false });
      },

      toggleRique: () => {
        const { isOpen } = get();
        if (isOpen) {
          set({ isOpen: false, isMinimized: false, isExpanded: false, showHistory: false });
        } else {
          set({ isOpen: true, isMinimized: false });
        }
      },

      toggleMinimize: () => {
        set((s) => ({ isMinimized: !s.isMinimized, showHistory: false }));
      },

      toggleExpanded: () => {
        set((s) => ({ isExpanded: !s.isExpanded, isMinimized: false, showHistory: false }));
      },

      setExpanded: (expanded) => set({ isExpanded: expanded }),

      setShowHistory: (show) => set({ showHistory: show }),

      setCompanyId: (companyId) => set({ companyId }),

      setPageContext: (pageContext) => set({ pageContext }),

      setRoutePath: (routePath) => set({ routePath }),

      consumePendingPrompt: () => {
        const prompt = get().pendingPrompt;
        if (prompt) set({ pendingPrompt: null });
        return prompt;
      },
    }),
    {
      name: 'rique-chat-ui',
      partialize: (s) => ({ isMinimized: s.isMinimized, companyId: s.companyId }),
    }
  )
);
